// Flow: min-cost max-flow over hele nettet. DK1 og DK2 er to synkronområder, der kun hænger sammen
// via Storebælt (HVDC-kant), fordi man ikke kan bygge ledninger på tværs af områder.
//
// Omkostninger (heltal, så resultatet er eksakt og deterministisk):
//   vind/sol/havvind 0, norsk og svensk vandkraft 1000, tysk import 2000, kraftværk 3000, batteri 4000.
//   Hver ledning koster 1, så korte veje vinder ved lige.
//   Nødkapacitet (de sidste 30 % op til 130 %) koster 5000 og findes kun i et nødvindue efter et udfald.
//   Udkobling efter tur: hver by får først 90 % (det, der holder den tændt), i rækkefølge fra den
//   mindste by til den største, og først derefter de sidste 10 %. Et lille underskud lander derfor hos
//   den største by, der bedst kan bære det, i stedet for at trække alle byer under 90 % på én gang.
//   Det er sådan, rigtig udkobling virker: nogle kunder ad gangen, ikke alle lidt.
//
// Fase 2 sender overskydende grøn strøm til batterier og udland (eksport).

import { FCR_MW, fcrActive, NORWAY_MW, norwayNode } from './effects';
import { lineCap, nodeById, nominalCap } from './grid';
import type { Area, GridNode, Line, State } from './types';

export const COST_GREEN = 0;
export const COST_HYDRO = 1000;
export const COST_DE = 2000;
export const COST_PLANT = 3000;
export const COST_BATTERY = 4000;
/** Lynreserve: dyrere end batterier, billigere end nødkapacitet. */
export const COST_FCR = 4500;
export const COST_LINE = 1;
export const COST_EMERGENCY = 5000;
export const COST_CHUNK = 20000;
/** Andel (promille) af forbruget, der holder byen tændt, og som fordeles før resten. */
export const KEEP_PM = 900;
/** Nødkapacitet: naboerne må bære op til 130 % i 5 s efter et udfald. */
export const EMERGENCY_PM = 1300;
export const OVERLOAD_TRIP_TICKS = 50;
/** Kaskader: når en ledning falder af overbelastning, har naboer over 100 % kun så mange ticks tilbage. */
export const CASCADE_LEFT_TICKS = 20;
export const EMERGENCY_TICKS = 60;
/** Flow genberegnes højst én gang i sekundet. */
export const FLOW_MIN_INTERVAL = 10;
export const BIG = 1_000_000;

// --- Generisk min-cost max-flow (successive shortest paths, Dijkstra med potentialer) ----------------

export class MinCostFlow {
  readonly n: number;
  private head: number[];
  private nxt: number[] = [];
  readonly to: number[] = [];
  readonly cap: number[] = [];
  readonly cost: number[] = [];
  readonly flow: number[] = [];

  constructor(n: number) {
    this.n = n;
    this.head = new Array<number>(n).fill(-1);
  }

  /** Tilføj en rettet kant. Returnerer kantens indeks (den omvendte er indeks + 1). */
  addEdge(u: number, v: number, cap: number, cost: number): number {
    const e = this.to.length;
    this.to.push(v); this.cap.push(cap); this.cost.push(cost); this.flow.push(0); this.nxt.push(this.head[u] ?? -1); this.head[u] = e;
    this.to.push(u); this.cap.push(0); this.cost.push(-cost); this.flow.push(0); this.nxt.push(this.head[v] ?? -1); this.head[v] = e + 1;
    return e;
  }

  /** Kør til max-flow. Alle startomkostninger skal være >= 0. Returnerer { flow, cost }. */
  run(s: number, t: number, maxFlow = Infinity): { flow: number; cost: number } {
    const n = this.n;
    const h = new Array<number>(n).fill(0);
    let total = 0;
    let totalCost = 0;
    const INF = Number.MAX_SAFE_INTEGER;
    const distArr = new Array<number>(n);
    const prevE = new Array<number>(n);
    const done = new Array<boolean>(n);
    let guard = 0;
    while (total < maxFlow) {
      if (++guard > 100000) break;
      distArr.fill(INF);
      prevE.fill(-1);
      done.fill(false);
      distArr[s] = 0;
      // O(V^2) Dijkstra: grafen er lille (~50 noder), og rækkefølgen er fast.
      for (;;) {
        let u = -1;
        let best = INF;
        for (let i = 0; i < n; i++) {
          const d = distArr[i] as number;
          if (!done[i] && d < best) { best = d; u = i; }
        }
        if (u === -1) break;
        done[u] = true;
        const hu = h[u] as number;
        for (let e = this.head[u] ?? -1; e !== -1; e = this.nxt[e] ?? -1) {
          if ((this.cap[e] as number) - (this.flow[e] as number) <= 0) continue;
          const v = this.to[e] as number;
          if (done[v]) continue;
          const nd = best + (this.cost[e] as number) + hu - (h[v] as number);
          if (nd < (distArr[v] as number)) {
            distArr[v] = nd;
            prevE[v] = e;
          }
        }
      }
      if ((distArr[t] as number) === INF) break;
      for (let i = 0; i < n; i++) if ((distArr[i] as number) < INF) h[i] = (h[i] as number) + (distArr[i] as number);
      // Flaskehals.
      let push = maxFlow - total;
      let v = t;
      while (v !== s) {
        const e = prevE[v] as number;
        push = Math.min(push, (this.cap[e] as number) - (this.flow[e] as number));
        v = this.to[e ^ 1] as number;
      }
      if (push <= 0) break;
      v = t;
      while (v !== s) {
        const e = prevE[v] as number;
        this.flow[e] = (this.flow[e] as number) + push;
        this.flow[e ^ 1] = (this.flow[e ^ 1] as number) - push;
        totalCost += push * (this.cost[e] as number);
        v = this.to[e ^ 1] as number;
      }
      total += push;
    }
    return { flow: total, cost: totalCost };
  }
}

// --- Produktion og forbrug ------------------------------------------------------------------------

export function genCost(n: GridNode): number {
  switch (n.kind) {
    case 'wind':
    case 'solar':
    case 'offshore':
      return COST_GREEN;
    case 'foreign':
      return (n.producer?.green ?? 0) >= 1 ? COST_HYDRO : COST_DE;
    case 'plant':
      // Et grønt værk (Studstrup på træpiller) kører før gassen, men efter vind og sol. Før havde det
      // samme pris som Skærbæk, så flowet brændte gas af, mens Studstrup stod stille (playtest runde 2).
      return (n.producer?.green ?? 0) >= 1 ? COST_HYDRO : COST_PLANT;
    default:
      return COST_PLANT;
  }
}

function isProducer(n: GridNode): boolean {
  return n.kind === 'wind' || n.kind === 'solar' || n.kind === 'offshore' || n.kind === 'plant' || n.kind === 'foreign';
}

function isConsumer(n: GridNode): boolean {
  return n.kind === 'city' || n.kind === 'datacenter';
}

/** MW byen skal have lige nu i flowet (0 i blackout-pausen). */
export function flowDemand(n: GridNode): number {
  if (!n.active || !n.city) return 0;
  if (n.city.darkTicks > 0) return 0;
  return Math.max(0, Math.round(n.city.demandMw));
}

export function lineArea(state: State, line: Line): Area {
  return nodeById(state, line.a)?.area ?? 'DK1';
}

export function inEmergency(state: State, area: Area): boolean {
  return state.tick <= state.flow.emergencyUntil[area];
}

// --- Selve beregningen ------------------------------------------------------------------------------

/**
 * Beregn flow for hele nettet og skriv resultatet i state: ledningernes flow og belastning, byernes
 * leverance, producenternes produktion og totaler. Rører ikke mørkeure eller rng.
 */
export function computeFlow(state: State): void {
  const nodes = state.nodes;
  const N = nodes.length;
  const S = N;
  const T = N + 1;
  // R = N + 2 er Lynreservens virtuelle node: den når alle forbrugere uden om ledningerne.
  const R = N + 2;
  const g = new MinCostFlow(N + 3);
  const idx = new Map<string, number>();
  nodes.forEach((n, i) => idx.set(n.id, i));

  // Kilder.
  const srcEdge: number[] = new Array<number>(N).fill(-1);
  const batEdge: number[] = new Array<number>(N).fill(-1);
  nodes.forEach((n, i) => {
    if (!n.active) return;
    if (isProducer(n) && n.producer) {
      const avail = Math.max(0, Math.round(n.producer.availMw));
      if (avail > 0) srcEdge[i] = g.addEdge(S, i, avail, genCost(n));
    }
    if (n.battery && n.battery.store > 0) {
      const mw = Math.min(n.battery.mw, Math.floor(n.battery.store));
      if (mw > 0) batEdge[i] = g.addEdge(S, i, mw, COST_BATTERY);
    }
  });

  // Power-ups: Ring til Norge er vandkraft ved Tjele; Lynreserven leverer direkte hos forbrugerne.
  let norwayEdge = -1;
  const nw = norwayNode(state);
  const nwi = nw ? idx.get(nw) : undefined;
  if (nwi !== undefined) norwayEdge = g.addEdge(S, nwi, NORWAY_MW, COST_HYDRO);
  if (fcrActive(state)) {
    g.addEdge(S, R, FCR_MW, COST_FCR);
    nodes.forEach((n, i) => {
      if (!isConsumer(n)) return;
      const d = flowDemand(n);
      if (d > 0) g.addEdge(R, i, d, 0);
    });
  }

  // Ledninger: to retninger, plus nødkapacitet i nødvinduet.
  const lineEdges: { line: Line; f: number; r: number; ef: number; er: number }[] = [];
  for (const line of state.lines) {
    const u = idx.get(line.a);
    const v = idx.get(line.b);
    const cap = lineCap(state, line);
    if (u === undefined || v === undefined || cap <= 0) {
      lineEdges.push({ line, f: -1, r: -1, ef: -1, er: -1 });
      continue;
    }
    const f = g.addEdge(u, v, cap, COST_LINE);
    const r = g.addEdge(v, u, cap, COST_LINE);
    let ef = -1;
    let er = -1;
    if (inEmergency(state, lineArea(state, line))) {
      const extra = Math.floor((cap * (EMERGENCY_PM - 1000)) / 1000);
      if (extra > 0) {
        ef = g.addEdge(u, v, extra, COST_EMERGENCY);
        er = g.addEdge(v, u, extra, COST_EMERGENCY);
      }
    }
    lineEdges.push({ line, f, r, ef, er });
  }

  // Dræn: hver forbruger får en "hold tændt"-spand (90 %) og en rest-spand (10 %). Holde-spandene
  // fyldes i tur fra mindste til største by; restspandene koster mest og fyldes sidst.
  const sinkEdges: number[][] = nodes.map(() => []);
  let demandTotal = 0;
  const order: { i: number; d: number; id: string }[] = [];
  nodes.forEach((n, i) => {
    if (!isConsumer(n)) return;
    const d = flowDemand(n);
    demandTotal += d;
    if (d > 0) order.push({ i, d, id: n.id });
  });
  order.sort((a, b) => a.d - b.d || (a.id < b.id ? -1 : 1));
  const last = (order.length + 1) * COST_CHUNK;
  order.forEach(({ i, d }, rank) => {
    const keep = Math.ceil((d * KEEP_PM) / 1000);
    const edges = sinkEdges[i] as number[];
    edges.push(g.addEdge(i, T, keep, (rank + 1) * COST_CHUNK));
    if (d - keep > 0) edges.push(g.addEdge(i, T, d - keep, last));
  });

  g.run(S, T);

  // Skriv resultater.
  let served = 0;
  let green = 0;
  nodes.forEach((n, i) => {
    if (n.city) {
      let s = 0;
      for (const e of sinkEdges[i] as number[]) s += g.flow[e] as number;
      n.city.servedMw = s;
      served += s;
    }
    if (n.producer) {
      const e = srcEdge[i] as number;
      const used = e >= 0 ? (g.flow[e] as number) : 0;
      n.producer.usedMw = used;
      green += used * n.producer.green;
    }
    if (n.battery) {
      const e = batEdge[i] as number;
      n.battery.usedMw = e >= 0 ? (g.flow[e] as number) : 0;
    }
  });

  if (norwayEdge >= 0) green += g.flow[norwayEdge] as number;

  const lineFlow = new Map<string, number>();
  for (const le of lineEdges) {
    let f = 0;
    if (le.f >= 0) f += (g.flow[le.f] as number) - (g.flow[le.r] as number);
    if (le.ef >= 0) f += (g.flow[le.ef] as number) - (g.flow[le.er] as number);
    lineFlow.set(le.line.id, f);
  }

  // Fase 2: overskydende grøn strøm til batterier og udland.
  const exportMw = surplusPhase(state, idx, lineFlow);

  // Ledningernes belastning.
  const deficitByArea: Record<Area, number> = { DK1: 0, DK2: 0 };
  for (const n of nodes) {
    if (n.city) deficitByArea[n.area] += Math.max(0, flowDemand(n) - n.city.servedMw);
  }
  for (const line of state.lines) {
    const f = lineFlow.get(line.id) ?? 0;
    line.flow = f;
    const nom = nominalCap(state, line);
    line.loadPm = nom > 0 && line.status === 'ok' ? Math.round((Math.abs(f) * 1000) / nom) : 0;
    line.congested = line.status === 'ok' && line.loadPm >= 990 && deficitByArea[lineArea(state, line)] > 0;
  }

  state.flow.demandMw = demandTotal;
  state.flow.servedMw = served;
  state.flow.greenMw = Math.round(green);
  state.flow.exportMw = exportMw;
  state.flow.lastTick = state.tick;
  state.flow.dirty = false;
}

/** Fase 2. Returnerer eksport i MW og lægger flowet oven i lineFlow. */
function surplusPhase(state: State, idx: Map<string, number>, lineFlow: Map<string, number>): number {
  const nodes = state.nodes;
  const N = nodes.length;
  const S = N;
  const T = N + 1;
  let anySink = false;
  for (const n of nodes) {
    if (!n.active) continue;
    if (n.kind === 'foreign') anySink = true;
    if (n.battery && n.battery.store < n.battery.storeMax) anySink = true;
  }
  if (!anySink) return 0;
  const g = new MinCostFlow(N + 2);
  let anySource = false;
  nodes.forEach((n, i) => {
    if (!n.active || !n.producer || n.kind === 'foreign' || n.kind === 'plant') return;
    if (n.producer.green < 1) return;
    const rest = Math.round(n.producer.availMw) - n.producer.usedMw;
    if (rest > 0) { g.addEdge(S, i, rest, 0); anySource = true; }
  });
  if (!anySource) return 0;
  const edges: { id: string; f: number; r: number }[] = [];
  for (const line of state.lines) {
    const u = idx.get(line.a);
    const v = idx.get(line.b);
    const cap = lineCap(state, line);
    if (u === undefined || v === undefined || cap <= 0) continue;
    const used = lineFlow.get(line.id) ?? 0; // + = a mod b
    const fwd = Math.max(0, cap - used);
    const rev = Math.max(0, cap + used);
    edges.push({ id: line.id, f: fwd > 0 ? g.addEdge(u, v, fwd, COST_LINE) : -1, r: rev > 0 ? g.addEdge(v, u, rev, COST_LINE) : -1 });
  }
  const exportEdges: number[] = [];
  const chargeEdges: { i: number; e: number }[] = [];
  nodes.forEach((n, i) => {
    if (!n.active) return;
    if (n.battery && n.battery.store < n.battery.storeMax) {
      chargeEdges.push({ i, e: g.addEdge(i, T, n.battery.mw, 0) });
    }
    if (n.kind === 'foreign') exportEdges.push(g.addEdge(i, T, BIG, 10));
  });
  g.run(S, T);
  for (const e of edges) {
    const add = (e.f >= 0 ? (g.flow[e.f] as number) : 0) - (e.r >= 0 ? (g.flow[e.r] as number) : 0);
    if (add !== 0) lineFlow.set(e.id, (lineFlow.get(e.id) ?? 0) + add);
  }
  for (const c of chargeEdges) {
    const n = nodes[c.i];
    if (n?.battery) n.battery.usedMw -= g.flow[c.e] as number; // negativ = lader
  }
  let exp = 0;
  for (const e of exportEdges) exp += g.flow[e] as number;
  return exp;
}

/**
 * Markér at flowet skal genberegnes ved næste lejlighed. Alt, der ændrer produktion, forbrug,
 * kapacitet eller nødvinduer, skal kalde denne (aldrig sætte `flow.dirty` direkte).
 */
export function markDirty(state: State): void {
  state.flow.dirty = true;
}

/** Start et nødvindue i et område (efter et udfald). */
/** Nødvinduer der lukker i dette tick: flowet skal regnes om uden de 130 %. */
export function endEmergencies(state: State): void {
  const e = state.flow.emergencyUntil;
  if (e.DK1 === state.tick - 1 || e.DK2 === state.tick - 1) markDirty(state);
}

export function startEmergency(state: State, area: Area): void {
  state.flow.emergencyUntil[area] = state.tick + EMERGENCY_TICKS;
  markDirty(state);
}
